import type { ArticleContent } from "./types";

export const articleDe: Record<string, ArticleContent> = {
  "basetao-spreadsheet-guide": {
    slug: "basetao-spreadsheet-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20clean%20workspace%20flat%20lay%20with%20a%20laptop%20showing%20a%20colorful%20spreadsheet%20grid%2C%20a%20notebook%2C%20a%20coffee%20cup%20and%20a%20folded%20sage%20green%20sweater%20on%20a%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20airy%20editorial%20photography%2C%20fresh%20minimal%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-guide&orientation=landscape&nocache=true",
    title: "Basetao Spreadsheet: Kompletter Leitfaden zu Produkten, Verkäufern & Kauf (2026)",
    cardDescription: "Wie die Basetao-Tabelle funktioniert, wie Kategorien organisiert sind und wie du zuverlässige Verkäufer findest.",
    metaDescription: "Erfahre, wie die Basetao-Tabelle funktioniert, durchstöbere Kategorien, finde zuverlässige Verkäufer und verstehe Versand und Qualitätskontrolle.",
    eyebrow: "Kaufleitfaden",
    intro: "Die Basetao-Tabelle ist ein geteiltes Verzeichnis, das Produktlinks, Verkäuferdetails und Kaufnotizen an einem Ort bündelt. Dieser Leitfaden erklärt ihren Aufbau, ihre Nutzung und wie du sicherer einkaufst.",
    sections: [
      {
        heading: "Was ist die Basetao-Tabelle?",
        paragraphs: [
          "Die Basetao-Tabelle ist eine kuratierte, von Menschen gepflegte Liste von Produkten und Verkäufern, die beim Einkauf auf internationalen Marktplätzen genutzt wird. Statt endlos zu stöbern, öffnen Käufer die Tabelle, um direkte Links, Kategorie-Shortcuts und Verkäufernotizen in einer Ansicht zu finden.",
          "Sie dient als Brücke zwischen Produktsuche und echtem Kauf. Jeder Eintrag verweist meist auf eine Live-Produktseite der Hauptwebsite, sodass die Tabelle dir hilft, schnell ans Ziel zu kommen.",
        ],
      },
      {
        heading: "Wie die Kategorien organisiert sind",
        paragraphs: [
          "Die Tabelle gruppiert Produkte in klare Kategorien wie Schuhe, Hoodies und Pullover, T-Shirts, Jacken, Hosen und Shorts, Kopfbedeckung, Sets, Unterwäsche, Trikots, Accessoires, Taschen, Gürtel und Parfums.",
          "Jede Kategorie verlinkt auf eine gefilterte Ansicht der Hauptwebsite, sodass du mit einem Klick von einer groben Idee zu einer konkreten Produktliste springst.",
        ],
      },
      {
        heading: "Zuverlässige Verkäufer finden",
        paragraphs: [
          "Achte auf Verkäufer mit vollständigen Angeboten, klaren Größenangaben und realistischen Fotos. Zuverlässige Einträge enthalten meist einen Link zur Produktseite, eine Notiz zu Größe oder Material und ein Aktualisierungsdatum.",
          "Ist ein Angebot vage oder ohne Details, sei vorsichtig. Die nützlichsten Einträge helfen dir, ein Produkt vor dem Kauf zu überprüfen.",
        ],
      },
      {
        heading: "Grundlagen zu Versand und Qualitätskontrolle",
        paragraphs: [
          "Prüfe vor dem Kauf die Größentabelle, die Materialbeschreibung und vorhandene Bewertungen. Vergleiche bei Kleidung die angegebenen Maße mit deinen eigenen.",
          "Versandzeiten und -kosten variieren je nach Artikel und Ziel. Bestätige immer Endpreis, geschätztes Lieferfenster und Rückgaberecht, bevor du bestellst.",
        ],
      },
    ],
    comparisonTitle: "Kurze Kategorienübersicht",
    comparisonHeaders: ["Kategorie", "Was prüfen", "Ideal für"],
    comparisonRows: [
      ["Schuhe", "Größe, Sohle, Farben", "Alltag & Streetwear"],
      ["Hoodies / Pullover", "Material, Gewicht, Passform", "Casual & Lagenlooks"],
      ["T-Shirts", "Stoff, Druckqualität, Passform", "Basics & Grafik-Shirts"],
      ["Jacken", "Isolierung, Wasserdicht, Größe", "Outdoor & Jahreszeiten"],
      ["Accessoires & Taschen", "Material, Riemen, Kapazität", "Feinschliff"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Ist die Basetao-Tabelle kostenlos?", a: "Ja. Die Tabelle ist ein Informationsverzeichnis, und die verlinkten Produktseiten sind auf der Hauptwebsite verfügbar." },
      { q: "Brauche ich ein Konto?", a: "Nein. Das Stöbern in Kategorien und Lesen der Leitfäden erfordert kein Konto. Kauf und Checkout laufen auf der Hauptwebsite." },
      { q: "Wie oft wird die Tabelle aktualisiert?", a: "Einträge werden regelmäßig geprüft, aber Verfügbarkeit und Preise können sich ändern. Prüfe immer die Live-Produktseite." },
      { q: "Ist diese Website mit den Verkäufern verbunden?", a: "Sie ist eine unabhängige Informationsressource und ersetzt nicht die Richtlinien der Hauptwebsite." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-review": {
    slug: "basetao-review",
    image:
      "https://readdy.ai/api/search-image?query=A%20modern%20desk%20scene%20with%20a%20tablet%20displaying%20a%20product%20listing%20and%20a%20small%20star%20rating%20graphic%20printed%20on%20paper%20beside%20a%20coffee%20cup%2C%20warm%20terracotta%20accents%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-review&orientation=landscape",
    title: "Basetao Test: Versand, Lager, QC & Sicherheit",
    cardDescription: "Ein ehrlicher Blick auf Versand, Lager, Qualitätskontrolle und Sicherheit von Basetao — was funktioniert und was zu beachten ist.",
    metaDescription: "Ein ehrlicher Basetao-Test zu Versand, Lager, Qualitätskontrolle und Käufersicherheit, plus was du vor der Bestellung prüfen solltest.",
    eyebrow: "Test",
    intro: "Dieser Test beleuchtet die wichtigsten Aspekte von Basetao — Versand, Lager, Qualitätskontrolle und Sicherheit — damit du entscheiden kannst, ob es zu deinem Einkaufsstil passt.",
    sections: [
      {
        heading: "Versand: Optionen und was dich erwartet",
        paragraphs: [
          "Basetao bietet mehrere Versandmethoden, die je nach Ziel, Geschwindigkeit und Kosten variieren. Express-Dienste kosten mehr, bieten aber detailliertes Tracking, während Sparoptionen günstiger und langsamer sind.",
          "Die beste Wahl hängt davon ab, wie schnell du den Artikel brauchst und wie viel du ausgeben möchtest. Prüfe immer das geschätzte Lieferfenster vor der Bestellung.",
        ],
      },
      {
        heading: "Lager und Auftragsabwicklung",
        paragraphs: [
          "Im Lager werden deine Artikel empfangen, geprüft und für den Versand vorbereitet. Ein gut geführtes Lager hält deine Bestellung zusammen und bestätigt, dass das Gekaufte dem Gesendeten entspricht.",
          "Klare Lager-Updates reduzieren Überraschungen. Kommt ein Artikel beschädigt oder abweichend an, sind Lagnotizen und Fotos die Grundlage für Rückerstattung oder Ersatz.",
        ],
      },
      {
        heading: "Qualitätskontrolle und Sicherheit",
        paragraphs: [
          "Qualitätskontrolle hilft, offensichtliche Probleme vor dem Versand zu erkennen, ist aber keine Garantie für Perfektion. Fotos und Checklisten sind nützlich, doch kleine Mängel können durchrutschen.",
          "Für Sicherheit zahle über anerkannte Zahlungsmethoden, bewahre Bestellunterlagen auf und prüfe die Verkäuferdetails vor größeren Käufen.",
        ],
      },
    ],
    comparisonTitle: "Basetao auf einen Blick",
    comparisonHeaders: ["Bereich", "Was gut funktioniert", "Was zu beachten ist"],
    comparisonRows: [
      ["Versand", "Mehrere Methoden und Tracking", "Kosten und Zeit je nach Methode"],
      ["Lager", "Konsolidierung und Prüfungen", "Abhängig vom Zustand bei Ankunft"],
      ["Qualitätskontrolle", "Fotos und Checklisten", "Keine Perfektionsgarantie"],
      ["Sicherheit", "Anerkannte Zahlungsmethoden", "Verkäufer immer selbst prüfen"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Ist Basetao ein zuverlässiger Dienst?", a: "Viele Käufer nutzen ihn erfolgreich, aber wie bei jedem Marktplatz kommt es auf Verkäufer und Artikel an. Recherche reduziert das Risiko." },
      { q: "Prüft Basetao jeden Artikel?", a: "Lagerprüfungen und Qualitätskontrolle helfen, garantieren aber nicht, dass jeder Artikel makellos ist." },
      { q: "Kann ich meine Basetao-Bestellung verfolgen?", a: "Die meisten Methoden bieten Tracking. Wähle einen verfolgbaren Dienst, um dein Paket bis zur Tür zu verfolgen." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "is-basetao-legit": {
    slug: "is-basetao-legit",
    image:
      "https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20resting%20over%20a%20printed%20document%20on%20a%20clean%20desk%20with%20a%20small%20padlock%20and%20a%20pen%2C%20soft%20peach%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-legit&orientation=landscape",
    title: "Ist Basetao seriös? Sicherheit, QC & Kaufleitfaden",
    cardDescription: "Eine klare Antwort, ob Basetao seriös ist, plus die Sicherheits- und Qualitätskontroll-Schritte, die dein Geld schützen.",
    metaDescription: "Ist Basetao seriös? Wir erklären Sicherheit, Qualitätskontrolle und Käuferschutz, damit du sicher einkaufen kannst.",
    eyebrow: "Sicherheitscheck",
    intro: "Eine häufige Frage neuer Käufer ist, ob Basetao seriös ist. Kurz gesagt: Es ist ein echter, viel genutzter Dienst, aber deine Sicherheit hängt davon ab, wie sorgfältig du einkaufst.",
    sections: [
      {
        heading: "Was einen Dienst seriös macht",
        paragraphs: [
          "Ein seriöser Dienst hat klare Richtlinien, echten Kundensupport und transparente Versand- und Zahlungsoptionen. Basetao bietet das, aber Verkäufer und Angebote variieren in der Qualität.",
          "Seriosität der Plattform macht nicht automatisch jedes Produkt oder jeden Verkäufer vertrauenswürdig. Behandle jeden Kauf als eigene Entscheidung.",
        ],
      },
      {
        heading: "So schützt du dein Geld",
        paragraphs: [
          "Nutze anerkannte Zahlungsmethoden mit Käuferschutz und bewahre jede Bestellung, Nachricht und Quittung auf. Vermeide Zahlungen außerhalb der Plattform oder über nicht nachverfolgbare Wege.",
          "Klingt ein Angebot zu gut, um wahr zu sein, ist es das meist. Vergleiche Preise und lies die Beschreibung genau, bevor du zahlst.",
        ],
      },
      {
        heading: "Die Rolle der Qualitätskontrolle",
        paragraphs: [
          "Qualitätskontrolle ist eine hilfreiche Ebene, die Artikel vor dem Versand auf offensichtliche Probleme prüft. Sie reduziert — aber beseitigt nicht — das Risiko eines falschen oder beschädigten Produkts.",
          "Frage nach QC-Fotos, wenn verfügbar, und melde Abweichungen sofort nach Erhalt, um die besten Chancen auf Lösung zu haben.",
        ],
      },
    ],
    comparisonTitle: "Seriositäts-Checkliste",
    comparisonHeaders: ["Prüfung", "Warum wichtig", "Was tun"],
    comparisonRows: [
      ["Zahlungsmethode", "Käuferschutz", "Anerkannte Optionen nutzen"],
      ["Verkäuferdetails", "Vertrauen und Verantwortung", "Angebote und Bewertungen lesen"],
      ["QC-Fotos", "Erkennt sichtbare Probleme", "Wenn möglich anfordern"],
      ["Bestellunterlagen", "Streitfall-Support", "Quittungen aufbewahren"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Ist Basetao ein Betrug?", a: "Nein. Basetao ist eine seriöse Plattform, aber du solltest einzelne Verkäufer und Produkte vor dem Kauf prüfen." },
      { q: "Was, wenn meine Bestellung nie ankommt?", a: "Kontaktiere den Support mit Bestelldetails und Tracking. Anerkannte Zahlungsmethoden bieten oft zusätzlichen Schutz." },
      { q: "Sollte ich QC-Fotos anfordern?", a: "Ja. Sie helfen, offensichtliche Probleme vor dem Versand zu erkennen und dienen als Beleg bei Problemen." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-shipping-cost": {
    slug: "basetao-shipping-cost",
    image:
      "https://readdy.ai/api/search-image?query=A%20stack%20of%20cardboard%20shipping%20boxes%20with%20a%20shipping%20label%20and%20a%20measuring%20tape%20on%20a%20warm%20sand%20table%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-shipping-cost&orientation=landscape",
    title: "Basetao Versandkosten: Kompletter Leitfaden & Vergleich",
    cardDescription: "Wie Basetao-Versandkosten berechnet werden, welche Methoden es gibt und wie du die günstigste zuverlässige Option wählst.",
    metaDescription: "Ein kompletter Leitfaden zu Basetao-Versandkosten — Berechnung, verfügbare Methoden und die Wahl der günstigsten zuverlässigen Option.",
    eyebrow: "Versand",
    intro: "Versandkosten sind oft das Erste, was Käufer verstehen wollen. Basetaos Preise hängen von Größe und Gewicht des Artikels, dem Ziel und der gewählten Methode ab.",
    sections: [
      {
        heading: "Wie Versandkosten berechnet werden",
        paragraphs: [
          "Die meisten Preise basieren auf dem tatsächlichen oder volumetrischen Gewicht des Pakets und der Entfernung. Größere, schwerere Artikel kosten naturgemäß mehr.",
          "Der Endpreis wird meist vor dem Bezahlen angezeigt, aber prüfe, ob Steuern oder Bearbeitungsgebühren enthalten sind.",
        ],
      },
      {
        heading: "Versandmethoden vergleichen",
        paragraphs: [
          "Sparmethoden sind am günstigsten, aber am langsamsten, während Express-Kuriere schneller und teurer sind. Standarddienste balancieren Kosten und Zeit.",
          "Die richtige Methode ist ein Kompromiss zwischen Budget und Tempo. Bei Geduld kann Sparversand bei größeren Bestellungen viel sparen.",
        ],
      },
      {
        heading: "Kosten senken",
        paragraphs: [
          "Mehrere Artikel zu einer Sendung zu bündeln senkt oft die Kosten pro Artikel. Auf eine größere Bestellung zu warten, macht Sparversand lohnender.",
          "Vergleiche die Gesamtkosten — Artikelpreis plus Versand — statt nur der Versandgebühr. Ein teurerer Artikel mit günstigerem Versand kann das bessere Geschäft sein.",
        ],
      },
    ],
    comparisonTitle: "Versandmethoden-Vergleich",
    comparisonHeaders: ["Methode", "Kosten", "Tempo", "Ideal für"],
    comparisonRows: [
      ["Sparversand", "Am niedrigsten", "Am langsamsten", "Budget, keine Eile"],
      ["Standard", "Moderat", "Ausgewogen", "Die meisten Bestellungen"],
      ["Express", "Am höchsten", "Am schnellsten", "Eilige oder wertvolle Artikel"],
      ["Gebündelt", "Günstiger pro Artikel", "Variabel", "Mehrere Artikel auf einmal"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Warum schwanken Versandkosten so stark?", a: "Die Kosten hängen von Gewicht und Größe des Pakets, dem Zielland und der gewählten Methode ab." },
      { q: "Ist es günstiger, mehrere Artikel zusammen zu versenden?", a: "Meist ja. Das Bündeln in ein Paket senkt oft die Kosten pro Artikel." },
      { q: "Sind Steuern im Versandpreis enthalten?", a: "Das variiert. Prüfe immer die Endpreis-Aufschlüsselung auf Steuern und Gebühren." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-shipping-guide": {
    slug: "basetao-shipping-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20small%20cardboard%20parcel%20with%20a%20shipping%20label%20next%20to%20a%20delivery%20van%20model%20and%20a%20clipboard%20on%20a%20soft%20olive%20green%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-shipping-guide&orientation=landscape",
    title: "Basetao Versandleitfaden: Methoden, Zeiten & Tracking",
    cardDescription: "Ein praktischer Leitfaden zu Basetao-Versandmethoden, Lieferzeiten und Tracking, damit du genau weißt, was dich erwartet.",
    metaDescription: "Verstehe Basetao-Versandmethoden, Lieferzeiten und Tracking. Lerne, den richtigen Dienst zu wählen und dein Paket zu verfolgen.",
    eyebrow: "Versandleitfaden",
    intro: "Den Versand vor der Bestellung zu verstehen, nimmt den meisten Stress des Wartens. Dieser Leitfaden behandelt die wichtigsten Methoden, typische Zeiten und das Tracking.",
    sections: [
      {
        heading: "Versandmethoden erklärt",
        paragraphs: [
          "Basetao versendet über eine Mischung aus Spar-, Standard- und Expressdiensten. Jeder balanciert Kosten, Tempo und Tracking-Niveau.",
          "Deine Wahl sollte Dringlichkeit und Sparziel widerspiegeln. Für die meisten Käufer ist ein verfolgbarer Standarddienst ein guter Mittelweg.",
        ],
      },
      {
        heading: "Lieferzeiten nach Methode",
        paragraphs: [
          "Lieferzeiten hängen von Methode und Ziel ab. Sparversand kann Wochen dauern, Expressoptionen können in Tagen ankommen.",
          "Betrachte Schätzungen als Richtwert, nicht als Versprechen. Zoll, Feiertage und Wetter können Zeit hinzufügen.",
        ],
      },
      {
        heading: "Wie Tracking funktioniert",
        paragraphs: [
          "Eine Tracking-Nummer lässt dich dein Paket vom Lager bis zur Tür verfolgen. Die meisten Dienste aktualisieren den Status an Schlüsselpunkten.",
          "Stoppt das Tracking, warte ein paar Tage vor dem Support-Kontakt, da internationale Scans manchmal hinterherhinken.",
        ],
      },
    ],
    comparisonTitle: "Lieferübersicht",
    comparisonHeaders: ["Methode", "Typische Zeit", "Tracking", "Gut zu wissen"],
    comparisonRows: [
      ["Sparversand", "2–6 Wochen", "Begrenzt", "Günstigste Option"],
      ["Standard", "1–3 Wochen", "Gut", "Ausgewogene Wahl"],
      ["Express", "3–7 Tage", "Detailliert", "Am schnellsten und teuersten"],
      ["Kurier", "Variabel", "Tür zu Tür", "Oft am zuverlässigsten"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Wie lange dauert der Basetao-Versand?", a: "Je nach Methode und Ziel, von wenigen Tagen bei Express bis zu mehreren Wochen bei Sparversand." },
      { q: "Bekomme ich eine Tracking-Nummer?", a: "Die meisten Standard- und Expressmethoden bieten Tracking. Sparoptionen können eingeschränktes Tracking haben." },
      { q: "Was, wenn mein Paket verspätet ist?", a: "Prüfe zuerst den Tracking-Status, dann kontaktiere den Support bei ungewöhnlich langer Stille." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-coupon-discount-code": {
    slug: "basetao-coupon-discount-code",
    image:
      "https://readdy.ai/api/search-image?query=A%20pair%20of%20scissors%20and%20a%20discount%20gift%20tag%20with%20a%20receipt%20and%20a%20small%20stack%20of%20coins%20on%20a%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-coupon&orientation=landscape",
    title: "Basetao Coupon & Rabattcode: Aktualisierter Leitfaden",
    cardDescription: "Wo du Basetao-Coupons und Rabattcodes findest, wie du sie nutzt und wie du abgelaufene Angebote vermeidest.",
    metaDescription: "Finde und nutze Basetao-Coupons und Rabattcodes. Erfahre, woher sie kommen, wie du sie anwendest und abgelaufene Angebote vermeidest.",
    eyebrow: "Sparen",
    intro: "Coupons und Rabattcodes senken einfach die Gesamtkosten einer Bestellung. Dieser Leitfaden erklärt ihre Herkunft, Nutzung und häufige Fallstricke.",
    sections: [
      {
        heading: "Woher Coupons und Codes kommen",
        paragraphs: [
          "Rabattcodes werden oft über Newsletter, saisonale Aktionen und die Hauptwebsite geteilt. Manche gelten seitenweit, andere nur für bestimmte Kategorien oder Mindestbestellwerte.",
          "Die zuverlässigsten Codes kommen aus offiziellen Quellen. Codes von Drittanbietern solltest du prüfen, da sie veraltet oder ungültig sein können.",
        ],
      },
      {
        heading: "Einen Rabattcode anwenden",
        paragraphs: [
          "Die meisten Codes gibst du im Checkout in einem eigenen Feld ein, bevor du die Zahlung bestätigst. Der Rabatt wird dann vom Gesamtbetrag abgezogen.",
          "Lies zuerst die Bedingungen, denn viele Codes erfordern einen Mindestbestellwert oder schließen Kategorien aus.",
        ],
      },
      {
        heading: "Abgelaufene oder ungültige Angebote vermeiden",
        paragraphs: [
          "Prüfe immer das Ablaufdatum und Mindestbestellwerte, bevor du deinen Warenkorb um einen Code baust. Ein letzte Woche abgelaufener Code funktioniert diese Woche nicht.",
          "Funktioniert ein Code nicht, prüfe Schreibweise und Bedingungen, statt das Angebot als falsch abzutun. Manche Rabatte sind case-sensitiv oder für Neukunden.",
        ],
      },
    ],
    comparisonTitle: "Rabattarten auf einen Blick",
    comparisonHeaders: ["Art", "Funktion", "Typische Anforderung", "Wo zu finden"],
    comparisonRows: [
      ["Prozentrabatt", "Reduziert Gesamtbetrag", "Oft Mindestbestellwert", "Newsletter & Aktionen"],
      ["Festbetrag", "Zieht Summe ab", "Mindestbestellwert", "Saisonale Kampagnen"],
      ["Gratisversand", "Entfernt Versandgebühr", "Kann Minimum erfordern", "Banner der Hauptwebsite"],
      ["Kategorie-Deal", "Gilt für eine Kategorie", "Kategoriespezifisch", "Produktseiten & Aktionen"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Wo finde ich einen Basetao-Rabattcode?", a: "Prüfe die Hauptwebsite, Newsletter und saisonale Aktionen. Offizielle Quellen sind am zuverlässigsten." },
      { q: "Warum funktioniert mein Code nicht?", a: "Er kann abgelaufen sein, einen Mindestbestellwert erfordern oder auf Kategorien oder Neukunden beschränkt sein." },
      { q: "Kann ich mehrere Coupons kombinieren?", a: "Meist nicht. Die meisten Shops erlauben nur einen Code pro Bestellung — wähle den mit der größten Ersparnis." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-warehouse-guide": {
    slug: "basetao-warehouse-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20clean%20bright%20warehouse%20aisle%20with%20neatly%20stacked%20cardboard%20boxes%20on%20steel%20shelves%20and%20a%20soft%20stone%20color%20palette%2C%20natural%20overhead%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-warehouse&orientation=landscape",
    title: "Basetao Lager-Leitfaden: So funktioniert die Lagerung",
    cardDescription: "Wie das Basetao-Lager deine Artikel empfängt, lagert und bündelt, bevor sie zu dir verschickt werden.",
    metaDescription: "Erfahre, wie das Basetao-Lager deine Artikel empfängt, lagert und bündelt, und wie es Versandzeit und Genauigkeit beeinflusst.",
    eyebrow: "Lager",
    intro: "Das Lager ist der Ort, an dem deine Artikel empfangen, gelagert und für den Versand vorbereitet werden. Dieses Verständnis hilft bei Bündelung und Lieferung.",
    sections: [
      {
        heading: "Wie das Lager Artikel empfängt",
        paragraphs: [
          "Wenn ein Verkäufer einen Artikel versendet, erreicht er das Lager und wird deiner Bestellung zugeordnet. Dieser Schritt bestätigt, dass der richtige Artikel ankam.",
          "Empfangsaufzeichnungen schaffen eine Papierspur. Fehlt etwas oder ist falsch, ist das Lagerprotokoll oft der erste Blickpunkt.",
        ],
      },
      {
        heading: "Lagerung und Bündelung",
        paragraphs: [
          "Artikel werden gelagert, bis du sie versenden möchtest. Kaufst du bei mehreren Verkäufern, kann das Lager alles in ein Paket bündeln — das senkt oft die Versandkosten.",
          "Bündelung ist besonders bei internationalen Bestellungen wertvoll, wo mehrere kleine Pakete viel teurer wären als eine Box.",
        ],
      },
      {
        heading: "Deine Bestellung für den Versand vorbereiten",
        paragraphs: [
          "Vor dem Versand bestätigt das Personal den Inhalt und bereitet das Paket vor. Fotos und Checklisten helfen hier, offensichtliche Probleme früh zu erkennen.",
          "Ein gut vorbereitetes Paket kommt seltener beschädigt an. Frage bei zerbrechlichen oder wertvollen Artikeln nach Verpackung und Fotos.",
        ],
      },
    ],
    comparisonTitle: "Lager-Workflow",
    comparisonHeaders: ["Schritt", "Was passiert", "Warum wichtig"],
    comparisonRows: [
      ["Empfang", "Artikel werden zugeordnet", "Bestätigt den richtigen Artikel"],
      ["Lagerung", "Artikel bis zum Versand", "Warten und kombinieren"],
      ["Bündelung", "Mehrere Artikel vereint", "Senkt Kosten pro Artikel"],
      ["Verpackung", "Inhalt geprüft und verpackt", "Reduziert Schäden und Fehler"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Wie lange bleiben Artikel im Lager?", a: "Lagerfenster variieren — prüfe die aktuelle Richtlinie, um Gebühren oder automatische Rücksendungen zu vermeiden." },
      { q: "Was ist Bündelung?", a: "Bündelung vereint Artikel mehrerer Verkäufer in ein Paket, was meist die Gesamtkosten senkt." },
      { q: "Prüft das Lager meine Artikel?", a: "Basisprüfungen sind üblich, aber detaillierte Qualitätskontrolle ist oft ein separater Schritt, den du anfordern kannst." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-qc-guide": {
    slug: "basetao-qc-guide",
    image:
      "https://readdy.ai/api/search-image?query=Two%20hands%20inspecting%20a%20folded%20garment%20with%20a%20checklist%20clipboard%20and%20a%20measuring%20tape%20on%20a%20clean%20desk%2C%20soft%20sage%20green%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-qc&orientation=landscape",
    title: "Basetao QC-Leitfaden: Qualitätskontrolle erklärt",
    cardDescription: "Was die Basetao-Qualitätskontrolle wirklich prüft, wie du QC-Fotos anforderst und was der Prozess kann und nicht kann.",
    metaDescription: "Verstehe die Basetao-Qualitätskontrolle — was sie prüft, wie du QC-Fotos anforderst und was sie garantieren kann und was nicht.",
    eyebrow: "Qualitätskontrolle",
    intro: "Qualitätskontrolle (QC) ist der Schritt, in dem deine Artikel vor dem Versand auf offensichtliche Probleme geprüft werden. Es ist eines der nützlichsten Werkzeuge für sicheres Einkaufen.",
    sections: [
      {
        heading: "Was die Qualitätskontrolle prüft",
        paragraphs: [
          "QC prüft meist, ob der Artikel dem Angebot entspricht, korrekt aussieht und keine offensichtlichen Schäden hat. Bei Kleidung umfasst das Größe, Farbe und Grundnähte.",
          "Das Ziel ist, klare Probleme zu erkennen, bevor ein Artikel um die Welt reist. Es ist kein tiefer Test, filtert aber viele häufige Probleme.",
        ],
      },
      {
        heading: "QC-Fotos anfordern",
        paragraphs: [
          "Viele Dienste erlauben Fotos deiner Artikel vor dem Versand. Sie zeigen dir genau, was du erhältst, und bestätigen die Übereinstimmung mit dem Angebot.",
          "Prüfe die Fotos genau. Wirkt etwas falsch — falsche Farbe, sichtbarer Defekt oder anderer Stil — lässt es sich vor dem Versand viel leichter beheben.",
        ],
      },
      {
        heading: "Was QC kann und nicht kann",
        paragraphs: [
          "QC erkennt sichtbare Probleme, kann aber Haltbarkeit, Stoffgefühl oder Langzeitqualität nicht testen. Kleine versteckte Mängel können eine Sichtprüfung passieren.",
          "Nutze QC als Teil deiner Entscheidung, nicht als Garantie. Kombiniere es mit sorgfältiger Angebotsprüfung und zuverlässiger Verkäuferwahl.",
        ],
      },
    ],
    comparisonTitle: "QC-Prozess-Übersicht",
    comparisonHeaders: ["Schritt", "Was geprüft wird", "Grenze"],
    comparisonRows: [
      ["Abgleich", "Artikel vs. Angebot", "Versteckte Details nicht prüfbar"],
      ["Sichtprüfung", "Offensichtliche Schäden", "Kleine Mängel möglich"],
      ["Größe / Farbe", "Basis-Spezifikationen", "Passform hängt von dir ab"],
      ["Fotos", "Was du erhältst", "Kein Haltbarkeitstest"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Lohnt sich Qualitätskontrolle?", a: "Ja, besonders bei Kleidung und wertvollen Artikeln. Sie erkennt viele offensichtliche Probleme vor dem Versand." },
      { q: "Garantiert QC einen perfekten Artikel?", a: "Nein. QC erkennt sichtbare Probleme, kann aber Haltbarkeit, Gefühl oder Langzeitqualität nicht testen." },
      { q: "Wie bekomme ich QC-Fotos?", a: "Fordere sie beim Aufgeben oder Prüfen deiner Bestellung an, wenn die Option verfügbar ist." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-safety-guide": {
    slug: "basetao-safety-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20small%20padlock%20and%20a%20shield-shaped%20wooden%20token%20on%20a%20clean%20desk%20with%20a%20notebook%20and%20pen%2C%20soft%20warm%20gray%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-safety&orientation=landscape",
    title: "Basetao Sicherheits- & Betrugspräventions-Leitfaden",
    cardDescription: "Praktische Schritte für sicheres Einkaufen auf Basetao — sichere Zahlungen, verdächtige Verkäufer erkennen und dein Konto schützen.",
    metaDescription: "Bleib sicher auf Basetao mit praktischen Betrugspräventions-Tipps: sichere Zahlungen, verdächtige Verkäufer erkennen und dein Konto schützen.",
    eyebrow: "Sicherheit",
    intro: "Sicheres Einkaufen ist vor allem eine Frage guter Gewohnheiten. Dieser Leitfaden behandelt sichere Zahlungen, das Erkennen verdächtiger Verkäufer und den Schutz deines Kontos.",
    sections: [
      {
        heading: "Sichere Zahlungsmethoden nutzen",
        paragraphs: [
          "Zahle immer über anerkannte, nachverfolgbare Methoden mit Käuferschutz. Vermeide direkte Zahlungen an Verkäufer oder nicht nachverfolgbare Überweisungsdienste.",
          "Bittet dich jemand, außerhalb der Plattform oder über eine ungewöhnliche Methode zu zahlen, ist das ein Warnsignal — geh nicht darauf ein.",
        ],
      },
      {
        heading: "Verdächtige Verkäufer erkennen",
        paragraphs: [
          "Verdächtige Verkäufer haben oft vage Angebote, unrealistische Preise oder drängen dich zu schnellem Handeln. Ein weit unter dem Marktdurchschnitt liegender Preis ist selten ein echtes Schnäppchen.",
          "Prüfe die Verkäuferdetails, lies die volle Beschreibung und suche nach konsistenten Informationen. Vertrauenswürdige Verkäufer liefern klare, spezifische Angebote.",
        ],
      },
      {
        heading: "Dein Konto schützen",
        paragraphs: [
          "Nutze ein starkes, einzigartiges Passwort und aktiviere verfügbare Sicherheitsfunktionen. Teile niemals deine Zugangsdaten oder Einmalcodes, auch nicht mit angeblichem Support.",
          "Bewahre Bestellungen, Nachrichten und Zahlungen auf. Gute Dokumentation erleichtert die Lösung eines Streitfalls enorm.",
        ],
      },
    ],
    comparisonTitle: "Sicherheits-Checkliste",
    comparisonHeaders: ["Gewohnheit", "Warum hilfreich", "Zu vermeidendes Warnsignal"],
    comparisonRows: [
      ["Anerkannte Zahlungen", "Käuferschutz", "Direkte oder nicht nachverfolgbare Überweisungen"],
      ["Verkäufer prüfen", "Verantwortung", "Vage oder fehlende Angebote"],
      ["Beschreibungen lesen", "Überraschungen vermeiden", "Unrealistisch niedrige Preise"],
      ["Unterlagen aufbewahren", "Leichtere Streitfälle", "Verlauf früh löschen"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Wie zahle ich am sichersten auf Basetao?", a: "Nutze anerkannte, nachverfolgbare Zahlungsmethoden mit Käuferschutz. Vermeide direkte Überweisungen." },
      { q: "Wie erkenne ich einen verdächtigen Verkäufer?", a: "Achte auf vage Angebote, unrealistische Preise und Druck zu schnellem Handeln." },
      { q: "Was tun bei Betrugsverdacht?", a: "Stoppe die Transaktion, melde den Verkäufer über die Plattform und kontaktiere den Support mit deinen Unterlagen." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-buying-guide": {
    slug: "basetao-buying-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20paper%20shopping%20bag%20with%20a%20folded%20sweater%20and%20a%20pair%20of%20shoes%20beside%20a%20tablet%20on%20a%20warm%20blush%20pink%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-buying&orientation=landscape",
    title: "Basetao Kauflietfaden: Vom Stöbern zum Checkout",
    cardDescription: "Ein Schritt-für-Schritt-Basetao-Kauflietfaden — von der Kategorie bis zu QC-Fotos und sicherem Checkout.",
    metaDescription: "Ein Schritt-für-Schritt-Basetao-Kauflietfaden zu Kategorie, Verkäuferwahl, Qualitätskontrolle und sicherem Checkout.",
    eyebrow: "Kaufleitfaden",
    intro: "Der Kauf über Basetao ist einfach, wenn man den Ablauf kennt. Dieser Leitfaden begleitet dich von der ersten Suche bis zum sicheren Checkout.",
    sections: [
      {
        heading: "Mit der richtigen Kategorie starten",
        paragraphs: [
          "Stöbere zuerst nach Kategorie — Schuhe, Hoodies, T-Shirts, Jacken, Taschen und mehr — um eine breite Idee in eine fokussierte Produktliste zu verwandeln. Jede Kategorie öffnet eine gefilterte Ansicht auf der Hauptwebsite.",
          "Frühes Eingrenzen spart Zeit. Je klarer dein Wunsch, desto leichter vergleichst du passende Optionen.",
        ],
      },
      {
        heading: "Verkäufer wählen und Angebot prüfen",
        paragraphs: [
          "Lies die volle Beschreibung, prüfe Größenangaben und schau dir die Fotos an. Ein gutes Angebot ist spezifisch zu Material, Maßen und Inhalt.",
          "Vergleiche mehrere Verkäufer statt das erste Ergebnis zu nehmen. Kleine Preis- oder Detailunterschiede können viel ausmachen.",
        ],
      },
      {
        heading: "Mit QC und sicherem Checkout abschließen",
        paragraphs: [
          "Fordere QC-Fotos an, wenn verfügbar, und prüfe sie vor dem Versand. Schließe dann den Checkout über eine anerkannte, nachverfolgbare Zahlungsmethode ab.",
          "Prüfe Versandmethode, geschätztes Lieferfenster und Gesamtkosten vor der Bestätigung. Ein paar Sekunden mehr verhindern die meisten Probleme.",
        ],
      },
    ],
    comparisonTitle: "Kaufablauf auf einen Blick",
    comparisonHeaders: ["Schritt", "Was du tust", "Wichtiger Tipp"],
    comparisonRows: [
      ["Stöbern", "Kategorie wählen", "Mit klarer Idee starten"],
      ["Auswählen", "Verkäufer vergleichen", "Volle Beschreibung lesen"],
      ["Prüfen", "Größen & Fotos prüfen", "QC anfordern, wenn möglich"],
      ["Bezahlen", "Sicher zahlen", "Gesamt und Versand bestätigen"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Wie starte ich den Einkauf auf Basetao?", a: "Wähle eine Kategorie, stöbere die gefilterte Liste auf der Hauptwebsite und vergleiche einige Optionen." },
      { q: "Sollte ich Qualitätskontrolle anfordern?", a: "Ja, besonders bei Kleidung oder wertvollen Artikeln. Sie hilft, offensichtliche Probleme vor dem Versand zu erkennen." },
      { q: "Was prüfe ich vor dem Bezahlen?", a: "Bestätige Artikeldetails, Versandmethode, geschätzte Zeit und Gesamtkosten vor dem Checkout." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },

  "basetao-seller-guide": {
    slug: "basetao-seller-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20minimal%20storefront%20concept%20with%20a%20small%20wooden%20sign%2C%20a%20handshake%20figurine%20and%20product%20cards%20on%20a%20warm%20khaki%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-seller&orientation=landscape",
    title: "Basetao Verkäufer-Leitfaden: Zuverlässige Verkäufer finden",
    cardDescription: "Wie du zuverlässige Basetao-Verkäufer erkennst, Angebote kritisch liest und vor dem Kauf vergleichst.",
    metaDescription: "Lerne, zuverlässige Basetao-Verkäufer zu finden, Angebote kritisch zu lesen und Optionen vor dem Kauf zu vergleichen.",
    eyebrow: "Verkäufer",
    intro: "Der gewählte Verkäufer hat großen Einfluss auf das, was du erhältst. Dieser Leitfaden erklärt, wie du zuverlässige Verkäufer erkennst und Angebote kritisch liest.",
    sections: [
      {
        heading: "Was einen Verkäufer zuverlässig macht",
        paragraphs: [
          "Zuverlässige Verkäufer liefern vollständige Angebote mit klaren Fotos, präzisen Größen und ehrlichen Materialangaben. Sie beantworten Fragen und bieten realistische Preise.",
          "Konsistenz ist ein starkes Signal. Ein Verkäufer mit detaillierten, korrekten Angeboten über mehrere Produkte ist oft sicherer als einer mit vagen Beschreibungen.",
        ],
      },
      {
        heading: "Ein Angebot kritisch lesen",
        paragraphs: [
          "Schau über das erste Foto hinaus und lies jedes Detail. Prüfe Größentabelle, Material und ob Fotos zur Beschreibung passen.",
          "Sei vorsichtig bei Angeboten mit nur Stockfotos, fehlenden Maßen oder ohne Antworten auf Grundfragen. Fehlende Details sind oft ein Zeichen.",
        ],
      },
      {
        heading: "Verkäufer vor dem Kauf vergleichen",
        paragraphs: [
          "Vergleiche mehrere Verkäufer für denselben Artikeltyp. Achte auf Preis, Versandzeit, Angebotsqualität und verfügbare Bewertungen.",
          "Die günstigste Option ist nicht immer die beste. Ein etwas höherer Preis bei einem detaillierteren, reaktionsschnellen Verkäufer kann Enttäuschungen ersparen.",
        ],
      },
    ],
    comparisonTitle: "Verkäufer-Bewertungscheckliste",
    comparisonHeaders: ["Faktor", "Zuverlässiges Signal", "Warnzeichen"],
    comparisonRows: [
      ["Angebotsdetail", "Klare Fotos und Größen", "Vage Beschreibung"],
      ["Preis", "Realistisch und konsistent", "Verdächtig niedrig"],
      ["Kommunikation", "Beantwortet Fragen", "Weicht aus oder ignoriert"],
      ["Fotos", "Passen zur Beschreibung", "Nur Stockfotos"],
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      { q: "Wie finde ich einen zuverlässigen Basetao-Verkäufer?", a: "Suche vollständige Angebote mit klaren Fotos, präzisen Größen und ehrlichen Beschreibungen. Vergleiche vor dem Kauf." },
      { q: "Was sind Warnzeichen eines schlechten Verkäufers?", a: "Vage Angebote, unrealistische Preise, nur Stockfotos und Verkäufer, die Fragen ausweichen." },
      { q: "Ist der günstigste Verkäufer immer die beste Wahl?", a: "Nein. Ein etwas höherer Preis bei einem detaillierteren Verkäufer führt oft zu einem besseren Kauf." },
    ],
    lastUpdated: "2026-08-19",
    author: "Basetao Spreadsheet Redaktion",
  },
};